import React, { useState } from "react";
import { FiX, FiClock, FiUser, FiInfo } from "react-icons/fi";

const AddSenderModal = ({ onClose, onAdd }) => {
  const [senderName, setSenderName] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Restricted sender IDs from the image
  const restrictedSenderIds = [
    "MTN balance",
    "AT Money",
    "Telecel Cash",
    "MoMo",
    "MoMo balance",
    "MTN MoMo",
    "Dear Customer",
    "Y'ello",
    "Yello",
    "Blocked",
    "Block",
    "Suspended",
    "Pin",
    "your mobile money account pin code has been block",
    "your mobile money account pin code has been blocked",
    "ATM card",
    "EcoBank",
    "Cherished User",
    "GCB"
  ];

  const validateSenderName = (name) => {
    setErrorMessage("");
    
    // Check against restricted sender IDs - exact phrase matches only
    const normalizedInput = name.trim().toLowerCase();
    
    for (const restrictedId of restrictedSenderIds) {
      const normalizedRestricted = restrictedId.toLowerCase();
      
      // Check if the input exactly matches a restricted ID
      if (normalizedInput === normalizedRestricted) {
        setIsValid(false);
        setErrorMessage(`"${name}" is a restricted sender ID. Please choose a different name.`);
        return false;
      }
      
      // Check if the input contains the entire restricted phrase as a complete phrase
      // This ensures "MTN MoMo" triggers but "MTN" alone doesn't
      if (normalizedInput.includes(normalizedRestricted)) {
        // Check if it's a complete phrase match (surrounded by spaces or at boundaries)
        const regex = new RegExp(`\\b${normalizedRestricted.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
        if (regex.test(normalizedInput)) {
          setIsValid(false);
          setErrorMessage(`"${name}" contains the restricted phrase "${restrictedId}". Please choose a different name.`);
          return false;
        }
      }
    }
    
    // Also check for common variations - only whole word matches
    const restrictedKeywords = [
      "mtn", "momo", "telecel", "ecobank", "gcb", 
      "blocked", "block", "suspended", "pin", "atm", "cherished",
      "y'ello", "yello", "dear customer", "at money", "telecel cash",
      "momomo", "mtn momo", "momo balance", "atm card"
    ];
    
    for (const keyword of restrictedKeywords) {
      // Create regex for whole word match
      const wordRegex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      
      // Check if the keyword appears as a complete word in the input
      if (wordRegex.test(normalizedInput)) {
        // Additional check: If it's a common word that might appear in legitimate business names
        // But block if it's the exact restricted phrase
        if (keyword.length >= 3) {
          setIsValid(false);
          setErrorMessage(`Sender ID cannot contain "${keyword}" as it's a reserved term. Please choose a different name.`);
          return false;
        }
      }
    }
    
    // Check for exact matches of multi-word restricted phrases
    const multiWordRestricted = [
      "mtn balance",
      "at money", 
      "telecel cash",
      "momo balance",
      "mtn momo",
      "dear customer",
      "atm card",
      "cherished user"
    ];
    
    for (const phrase of multiWordRestricted) {
      const phraseRegex = new RegExp(`\\b${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (phraseRegex.test(normalizedInput)) {
        setIsValid(false);
        setErrorMessage(`"${name}" contains the restricted phrase "${phrase}". Please choose a different name.`);
        return false;
      }
    }
    
    // Allow letters (both cases), numbers, and spaces, max 11 characters
    // Cannot be only numbers, no special characters
    const isValidFormat = /^[A-Za-z0-9\s]{1,11}$/.test(name);
    const hasLetters = /[A-Za-z]/.test(name);
    const isOnlyNumbers = /^\d+$/.test(name.replace(/\s/g, ""));

    const isValid = isValidFormat && hasLetters && !isOnlyNumbers;
    setIsValid(isValid);
    
    if (!isValid) {
      setErrorMessage("Invalid format - check guidelines below");
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateSenderName(senderName) && !errorMessage) {
      setIsSubmitting(true);
      try {
        const success = await onAdd(senderName);
        if (success) {
          onClose(); // Close modal only on success
        } else {
          setIsSubmitting(false); // Reset loading state on failure
        }
      } catch (error) {
        console.error("Error adding sender ID:", error);
        setIsSubmitting(false);
      }
    }
  };

  const guidelines = [
    "Maximum 11 characters allowed",
    "Letters (A-Z, a-z), numbers (0-9), and spaces permitted",
    "No special characters or symbols allowed",
    "Must contain at least one letter (cannot be only numbers)",
    "Use your business name or recognizable brand",
    "Spaces are allowed but will be removed in final format",
    "Case-insensitive (will be converted to uppercase)",
    "⚠️ Restricted: Cannot use telecom/bank service names or status messages",
    "⚠️ Blocked: Complete phrases like 'MTN MoMo', 'Blocked', 'ATM card' are not allowed"
  ];

  const examples = [
    { good: "My Store", bad: "123456789" },
    { good: "Pizza 123", bad: "PIZZA-123" },
    { good: "Bank Name", bad: "MTN MoMo" },
    { good: "Delivery Co", bad: "BLOCKED" },
    { good: "John Doe", bad: "ATM card" },
    { good: "Fast Food", bad: "Dear Customer" }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header - Fixed at top */}
        <div className="z-10 flex-shrink-0 p-6 bg-white rounded-t-2xl border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                <FiUser className="text-xl text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  Add New Sender ID
                </h3>
                <p className="text-sm text-gray-600">
                  Create a custom sender identity for your messages
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
            >
              <FiX className="text-2xl" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <form onSubmit={handleSubmit}>
            {/* Input Section */}
            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Sender ID Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => {
                    // Allow letters (both cases), numbers, and spaces, remove special characters
                    const value = e.target.value.replace(/[^A-Za-z0-9\s]/g, "");
                    setSenderName(value);
                    validateSenderName(value);
                  }}
                  placeholder="Enter your sender ID (e.g., My Store)"
                  className={`w-full px-4 py-3 border-2 rounded-xl outline-none transition-all text-lg font-mono ${
                    isValid && !errorMessage
                      ? "border-gray-200 focus:border-purple-500"
                      : "border-red-300 focus:border-red-500"
                  }`}
                  maxLength={11}
                />
                <div className="absolute right-4 top-1/2 text-gray-400 transform -translate-y-1/2">
                  {senderName.length}/11
                </div>
              </div>

              <div className="flex justify-between mt-2">
                <span
                  className={`text-sm ${
                    isValid && !errorMessage ? "text-gray-500" : "text-red-500"
                  }`}
                >
                  {errorMessage || (isValid
                    ? "Letters, numbers, and spaces allowed (max 11 characters)"
                    : "Invalid format - check guidelines below")}
                </span>
                <button
                  type="button"
                  onClick={() => setShowGuidelines(!showGuidelines)}
                  className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <FiInfo className="text-xs" />
                  <span>Guidelines</span>
                </button>
              </div>
            </div>

            {/* Error Message for Restricted Sender IDs */}
            {errorMessage && errorMessage.includes("restricted") && (
              <div className="p-4 mb-6 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-start space-x-3">
                  <FiInfo className="text-red-500 text-lg mt-0.5" />
                  <div>
                    <h4 className="mb-1 font-semibold text-red-800">
                      Restricted Sender ID
                    </h4>
                    <p className="text-sm text-red-700">
                      {errorMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Guidelines Section */}
            {showGuidelines && (
              <div className="p-4 mb-6 bg-blue-50 rounded-xl border border-blue-200">
                <h4 className="flex items-center mb-3 space-x-2 font-semibold text-blue-800">
                  <FiInfo className="text-lg" />
                  <span>Sender ID Guidelines</span>
                </h4>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <h5 className="mb-2 font-medium text-blue-700">
                      Requirements:
                    </h5>
                    <ul className="space-y-1 text-sm text-blue-700">
                      {guidelines.map((guideline, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="mt-1 text-blue-500">•</span>
                          <span>{guideline}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="mb-2 font-medium text-blue-700">
                      Examples:
                    </h5>
                    <div className="space-y-2">
                      {examples.map((example, index) => (
                        <div key={index} className="text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-green-600">✓</span>
                            <code className="px-2 py-1 font-mono text-green-700 bg-green-100 rounded">
                              {example.good}
                            </code>
                          </div>
                          <div className="flex items-center mt-1 space-x-2">
                            <span className="text-red-600">✗</span>
                            <code className="px-2 py-1 font-mono text-red-700 bg-red-100 rounded">
                              {example.bad}
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Only complete restricted phrases are blocked. 
                    For example, "MTN" alone is allowed, but "MTN MoMo" is blocked.
                  </p>
                </div>
              </div>
            )}

            {/* Preview Section */}
            {senderName && isValid && !errorMessage && (
              <div className="p-4 mb-6 bg-gray-50 rounded-xl border border-gray-200">
                <h4 className="mb-3 font-semibold text-gray-700">Preview</h4>
                <div className="p-4 bg-white rounded-lg border border-gray-300">
                  <div className="mb-1 text-sm text-gray-500">
                    How it will appear to recipients:
                  </div>
                  <div className="p-3 max-w-xs text-white bg-blue-600 rounded-lg">
                    <div className="mb-1 text-xs opacity-75">
                      From: {senderName}
                    </div>
                    <div className="text-sm">
                      Your message will appear here...
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Approval Notice */}
            <div className="p-4 mb-6 bg-yellow-50 rounded-xl border border-yellow-200">
              <div className="flex items-start space-x-3">
                <FiClock className="text-yellow-500 text-lg mt-0.5" />
                <div>
                  <h4 className="mb-1 font-semibold text-yellow-800">
                    Approval Required
                  </h4>
                  <p className="mb-2 text-sm text-yellow-700">
                    Custom sender IDs require approval from telecom operators
                    and may take 24-48 hours to activate.
                  </p>
                  <ul className="space-y-1 text-sm text-yellow-700">
                    <li>• You'll receive email notification once approved</li>
                    <li>• Use default sender ID while waiting</li>
                    <li>
                      • Rejections are rare but may occur for policy violations
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!senderName || !isValid || isSubmitting || !!errorMessage}
                className="flex-1 py-3 font-medium text-white bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl transition-all transform hover:from-purple-600 hover:to-pink-700 disabled:from-gray-300 disabled:to-gray-400 hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : "Submit for Approval"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSenderModal;